import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ProfileAvatar, ProfileCard } from "@/components/layout/ProfileCard";
import { profileDisplay, useProfile } from "@/components/layout/useProfile";
import { AuthModal } from "@/components/renewal/auth/AuthModal";
import "@/components/layout/topbar.css";

/**
 * Desktop top-bar auth surface.
 * Signed out → [Log in(고스트 텍스트) | Sign up(액센트 필)] — 각자 AuthModal을
 * 해당 모드로 연다. Signed in → avatar/name chip that toggles the ProfileCard
 * dropdown (anchored right, below the bar; closes on outside click / ESC /
 * route change). IP/RP/Rank only show inside that dropdown, fetched lazily
 * on first open — the bar itself carries no balance readout.
 */
export function ProfileChip() {
  const location = useLocation();
  const { user, authenticated, sessionPending, summary, ensureSummary, signOut } = useProfile();

  const [open, setOpen] = useState(false);
  const [authMode, setAuthMode] = useState<"login" | "signup" | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Route change always closes the dropdown.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (sessionPending) return null;

  if (!authenticated) {
    return (
      <div className="flex shrink-0 items-center gap-1.5">
        <button
          type="button"
          onClick={() => setAuthMode("login")}
          className="inline-flex h-9 items-center rounded-full px-3 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-ink-dim outline-none transition-colors duration-200 hover:text-ink focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-accent/60"
        >
          Log in
        </button>
        <button
          type="button"
          onClick={() => setAuthMode("signup")}
          className="ty-cta inline-flex h-9 items-center rounded-full px-4 font-mono text-[10px] font-medium uppercase tracking-[0.12em] outline-none focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-accent/60"
        >
          Sign up
        </button>
        <AuthModal
          open={authMode !== null}
          initialMode={authMode ?? "login"}
          onClose={() => setAuthMode(null)}
        />
      </div>
    );
  }

  const display = profileDisplay(user);

  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next) ensureSummary();
      return next;
    });
  };

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Profile menu"
        className="group flex h-9 items-center gap-2 rounded-full border border-line bg-white/4 pl-1 pr-3 outline-none transition-colors duration-200 hover:border-line-hi focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-accent/60"
      >
        <ProfileAvatar
          display={display}
          className="size-7 text-[10px] ring-1 ring-white/10 transition-shadow duration-200 group-hover:ring-accent/40"
        />
        <span className="max-w-36 truncate text-xs font-medium text-ink">{display.name}</span>
      </button>

      {open ? (
        <div className="ty-profile-pop absolute right-0 top-full z-50 mt-2">
          <ProfileCard
            display={display}
            summary={summary}
            onSignOut={() => {
              setOpen(false);
              void signOut();
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
